<template>
  <nav
    class="flex h-full flex-col border-r border-slate-200 bg-white shadow-soft"
  >
    <div
      :class="[
        'flex items-center gap-2 border-b border-slate-100 px-5 py-3',
        collapsed ? 'justify-center px-2' : 'justify-between',
      ]"
    >
      <router-link
        v-if="!collapsed"
        to="/home"
        :class="['flex items-center gap-2 overflow-hidden']"
        @click="$emit('navigate')"
      >
        <img
          src="../assets/logoMBF.png"
          alt="MBF"
          class="w-[70%] object-contain transition-all"
        />
      </router-link>
      <button
        type="button"
        class="hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:bg-brand-50 hover:text-brand-700 lg:flex"
        :title="collapsed ? 'Mở rộng sidebar' : 'Thu gọn sidebar'"
        @click="$emit('toggle-collapse')"
      >
        <component
          :is="collapsed ? PanelLeftOpen : PanelLeftClose"
          :size="16"
          :stroke-width="2.2"
        />
      </button>
    </div>

    <div
      :class="['min-h-0 flex-1 overflow-y-auto p-2.5', collapsed ? 'px-2' : '']"
    >
      <p
        v-if="!collapsed"
        class="px-2.5 pb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400"
      >
        Menu chức năng
      </p>

      <template v-for="entry in menuEntries" :key="entry.key">
        <router-link
          v-if="entry.type === 'item'"
          :to="entry.to"
          :title="collapsed ? entry.label : ''"
          :class="[
            'mb-1 flex items-center rounded-xl py-2 text-xs font-semibold text-slate-600 transition hover:bg-brand-50 hover:text-brand-700',
            collapsed ? 'justify-center px-0' : 'gap-2.5 px-2.5',
          ]"
          active-class="bg-brand-600 text-white hover:bg-brand-600 hover:text-white shadow-card"
          @click="$emit('navigate')"
        >
          <span
            class="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-current/10"
          >
            <component :is="entry.icon" :size="15" :stroke-width="2.2" />
          </span>
          <span v-if="!collapsed" class="truncate">{{ entry.label }}</span>
        </router-link>

        <div v-else class="mb-1">
          <button
            v-if="!collapsed"
            type="button"
            class="flex w-full items-center justify-between gap-2 rounded-xl px-2.5 py-2 text-[11px] font-bold text-slate-500 transition hover:bg-slate-50 hover:text-slate-700"
            @click="toggleGroup(entry.key)"
          >
            <span class="truncate text-left">{{ entry.label }}</span>
            <ChevronDown
              :size="15"
              :stroke-width="2.3"
              :class="[
                'shrink-0 transition-transform',
                openGroups[entry.key] ? 'rotate-180' : '',
              ]"
            />
          </button>

          <div
            v-show="collapsed || openGroups[entry.key]"
            class="mt-0.5 space-y-1"
          >
            <router-link
              v-for="item in entry.children"
              :key="item.to"
              :to="item.to"
              :title="collapsed ? item.label : ''"
              :class="[
                'flex items-center rounded-xl py-2 text-xs font-semibold text-slate-600 transition hover:bg-brand-50 hover:text-brand-700',
                collapsed ? 'justify-center px-0' : 'gap-2 px-2.5 pl-4',
              ]"
              active-class="bg-brand-600 text-white hover:bg-brand-600 hover:text-white shadow-card"
              @click="$emit('navigate')"
            >
              <span
                class="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-current/10"
              >
                <component :is="item.icon" :size="15" :stroke-width="2.2" />
              </span>
              <span v-if="!collapsed" class="truncate">{{ item.label }}</span>
            </router-link>
          </div>
        </div>
      </template>
    </div>

    <div :class="['border-t border-slate-100 p-3', collapsed ? 'px-2' : '']">
      <div
        :class="[
          'mb-2 rounded-xl bg-slate-100 p-2.5 text-left',
          collapsed ? 'flex h-10 items-center justify-center p-0' : '',
        ]"
        :title="collapsed ? fullName || permissionLabel : ''"
      >
        <p v-if="collapsed" class="text-xs font-bold text-ink">
          {{ initials }}
        </p>
        <p v-if="!collapsed" class="truncate text-xs font-bold text-ink">
          {{ fullName || "Người dùng" }}
        </p>
        <p v-if="!collapsed" class="truncate text-[11px] text-slate-500">
          {{ team || permissionLabel }}
        </p>
      </div>
      <UiButton
        variant="secondary"
        block
        :size="collapsed ? 'icon' : 'md'"
        icon="logout"
        :title="collapsed ? 'Đăng xuất' : ''"
        @click="logout"
      >
        <span v-if="!collapsed">Đăng xuất</span>
      </UiButton>
    </div>
  </nav>
</template>

<script lang="ts">
import { computed, reactive, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import router from "@/router";
import {
  BarChart3,
  Boxes,
  Building2,
  ChevronDown,
  ClipboardCheck,
  ClipboardPlus,
  ClipboardList,
  FileText,
  Layers3,
  ListTree,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  UserPlus,
  Users,
} from "lucide-vue-next";

type MenuItem = {
  type: "item";
  key: string;
  label: string;
  icon: unknown;
  to: string;
};

type MenuGroup = {
  type: "group";
  key: string;
  label: string;
  children: Array<Omit<MenuItem, "type" | "key">>;
};

type MenuEntry = MenuItem | MenuGroup;

export default {
  components: { ChevronDown, LogOut },
  props: {
    collapsed: { type: Boolean, default: false },
    fullName: { type: String, default: "" },
    team: { type: String, default: "" },
  },
  emits: ["navigate", "toggle-collapse"],
  setup(props: any) {
    const store = useStore();
    const route = useRoute();
    const deviceGroup: MenuGroup = {
      type: "group",
      key: "device-management",
      label: "Quản lý thiết bị",
      children: [
        {
          label: "Tiếp nhận thiết bị",
          icon: ClipboardPlus,
          to: "/inputDevice",
        },
        {
          label: "Tình trạng thiết bị",
          icon: ClipboardCheck,
          to: "/managerDevicePage",
        },
        { label: "Tất cả thiết bị", icon: Boxes, to: "/allDevicePage" },
      ],
    };

    const categoryGroup: MenuGroup = {
      type: "group",
      key: "category-management",
      label: "Quản lý danh mục",
      children: [
        { label: "Khoa phòng", icon: Building2, to: "/departments" },
        { label: "Nhóm thiết bị", icon: Layers3, to: "/device-groups" },
        {
          label: "Chi tiết loại thiết bị",
          icon: ListTree,
          to: "/device-type-details",
        },
      ],
    };

    const maintenanceItem: MenuItem = {
      type: "item",
      key: "maintenance",
      label: "Bảo trì bảo dưỡng",
      icon: ClipboardList,
      to: "/maintenance-history",
    };

    const adminMenuEntries: MenuEntry[] = [
      {
        type: "item",
        key: "overview",
        label: "Tổng quan",
        icon: BarChart3,
        to: "/chartPage",
      },
      deviceGroup,
      categoryGroup,
      maintenanceItem,
      {
        type: "item",
        key: "reports",
        label: "Báo cáo",
        icon: FileText,
        to: "/reportPage",
      },
      {
        type: "item",
        key: "users",
        label: "Tài khoản",
        icon: Users,
        to: "/userlist",
      },
      {
        type: "item",
        key: "register",
        label: "Đăng ký tài khoản",
        icon: UserPlus,
        to: "/registerpage",
      },
    ];

    const userMenuEntries: MenuEntry[] = [
      {
        type: "item",
        key: "overview",
        label: "Tổng quan",
        icon: BarChart3,
        to: "/chartPage",
      },
      {
        ...deviceGroup,
        children: deviceGroup.children.filter(
          (item) => item.to !== "/inputDevice",
        ),
      },
      maintenanceItem,
    ];

    const openGroups = reactive<Record<string, boolean>>({
      "device-management": true,
      "category-management": true,
    });

    const menuEntries = computed(() =>
      store.state.permission === "admin" ? adminMenuEntries : userMenuEntries,
    );
    const permissionLabel = computed(() =>
      store.state.permission === "admin" ? "Quản trị viên" : "Người dùng",
    );
    const initials = computed(() => {
      const source = props.fullName || "QL";
      return source
        .split(" ")
        .filter(Boolean)
        .slice(-2)
        .map((part: string) => part.charAt(0).toUpperCase())
        .join("");
    });

    const syncOpenGroups = () => {
      menuEntries.value.forEach((entry) => {
        if (entry.type !== "group") return;
        if (entry.children.some((item) => item.to === route.path)) {
          openGroups[entry.key] = true;
        }
      });
    };

    const toggleGroup = (key: string) => {
      openGroups[key] = !openGroups[key];
    };

    const logout = () => {
      store.dispatch("clearToken");
      store.dispatch("clearPermission");
      store.dispatch("clearFullName");
      store.dispatch("clearTeam");
      router.push({ name: "login" });
    };

    watch(() => route.path, syncOpenGroups, { immediate: true });

    return {
      menuEntries,
      initials,
      logout,
      openGroups,
      PanelLeftClose,
      PanelLeftOpen,
      permissionLabel,
      toggleGroup,
    };
  },
};
</script>
